import type { Clarification } from '@/api/clarifications';
/**
 * Kontroll's record panel (V7.5.6, #275 D098 to D140): the status, the question to the employee when there is
 * one, the case, planned against recorded, the facts, then the original registration and its history. One
 * footer row: the captured queue's arrows on the left, the actions on the right, a note under them. A decision
 * (confirm as correct, ask, record an answer) opens its box after the facts and takes the footer: «Avbryt» and
 * the decision. «Rett registrering» opens the correction inline: the actual clock and break times only, a live
 * result, a required reason, «Avbryt retting» and «Lagre retting» (PUT /admin/shifts/:id, bound to the values
 * shown). Saving a correction never approves. On an approved record «Trekk tilbake godkjenning» asks why and
 * takes the approval back (PUT /admin/shifts/:id/accept false, bound to the values shown; a closed pay month
 * refuses it). An expected shift with no registration (a gap) opens like a review record, its actual fields blank.
 */
import { errorText } from '@/api/client';
import type { ApiShift, ApiWorkTimeChange } from '@/api/data';
import { revisionOf } from '@/api/shifts';
import { T } from '@/i18n';
import { oslo, osloISO, plannedPaidHours, worked, workedResult } from '@/rules';
import { useDirty } from '@/shell/context';
import { Button, Menu } from '@/ui';
import { num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { DetailPanel } from '@/ui/page';
import type { BreakPolicy } from '@timefly/core';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import History from '../arbeidstid/History';
import { deliveryText } from './KontrollClarify';
import { Status } from './KontrollQueue';
import { NONE, clockOf, dateLong, dateShort, dateTimeLong, hoursText, interval } from './kfmt';
import type { Rec } from './kontroll';

type Mode = null | 'edit' | 'approve' | 'clarify' | 'reply' | 'reopen';
export interface Correction {
  punch_in_time: string;
  punch_out_time: string;
  lunch_in_time: string | null;
  lunch_out_time: string | null;
  change_reason: string;
  if_unchanged: ReturnType<typeof revisionOf>;
}

const local = (iso: string | null | undefined) => {
  const o = oslo(iso);
  return o ? `${o.date}T${o.time}` : '';
};
const instant = (v: string) => (v ? osloISO(v.slice(0, 10), v.slice(11, 16)) : '');

/** the first value each worked-time field held, from the oldest change that touched it; else as it is */
function originalOf(s: ApiShift, rows: ApiWorkTimeChange[]) {
  const out: Record<string, unknown> = {
    punch_in_time: s.punch_in_time,
    punch_out_time: s.punch_out_time,
    lunch_in_time: s.lunch_in_time,
    lunch_out_time: s.lunch_out_time,
  };
  const seen = new Set<string>();
  for (const c of [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at)))
    for (const [f, pair] of Object.entries(c.changes ?? {}))
      if (f in out && !seen.has(f)) {
        seen.add(f);
        out[f] = pair[0];
      }
  return out as {
    punch_in_time: string | null;
    punch_out_time: string | null;
    lunch_in_time: string | null;
    lunch_out_time: string | null;
  };
}

export function KontrollRecord({
  r,
  queue,
  onGo,
  onClose,
  policy,
  name,
  sub,
  clar,
  history,
  entries,
  writable,
  mayEdit,
  mayApprove,
  mayAsk,
  busy,
  onApprove,
  onReopen,
  onCorrect,
  onAsk,
  onReply,
  onWithdraw,
  onResend,
  menu,
  extraFacts,
  startEdit,
}: {
  /** opened from a payroll check (?rett=): the correction opens at once on that clock field */
  startEdit?: 'in' | 'out' | null;
  r: Rec;
  /** the queue captured when the panel opened */
  queue: number[];
  onGo: (id: number) => void;
  onClose: () => void;
  policy: BreakPolicy | null;
  name: string;
  sub: string;
  clar: Clarification | undefined;
  /** null while loading, undefined when it could not be read */
  history: ApiWorkTimeChange[] | null | undefined;
  /** the record's rule lines (kept: real checks, D107) */
  entries: string[];
  /** a fresh read: false when the shown data is stale or failed, and every write is off */
  writable: boolean;
  mayEdit: boolean;
  mayApprove: boolean;
  /** asking is offered to this manager for this record (not their own shift) */
  mayAsk: boolean;
  busy: boolean;
  onApprove: (next: boolean, reason?: string) => Promise<boolean>;
  /** take an approval back with a reason; throws on a refusal */
  onReopen: (reason: string) => Promise<void>;
  onCorrect: (c: Correction) => Promise<void>;
  onAsk: (question: string) => Promise<void>;
  onReply: (reply: string) => Promise<void>;
  onWithdraw: () => void;
  onResend: () => void;
  menu: { key: string; label: string; onSelect: () => void }[];
  extraFacts?: ReactNode;
}) {
  const s = r.s;
  const [mode, setMode] = useState<Mode>(null);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({
    in: local(s.punch_in_time),
    out: local(s.punch_out_time),
    lin: local(s.lunch_in_time),
    lout: local(s.lunch_out_time),
    reason: '',
  }));
  const errRef = useRef<HTMLDivElement>(null);
  // a new record, or the same one moved by another write: start from what it holds now
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the record's identity and stored times only
  useEffect(() => {
    setMode(null);
    setText('');
    setErr(null);
    setDraft({
      in: local(s.punch_in_time),
      out: local(s.punch_out_time),
      lin: local(s.lunch_in_time),
      lout: local(s.lunch_out_time),
      reason: '',
    });
  }, [s.id, s.punch_in_time, s.punch_out_time, s.lunch_in_time, s.lunch_out_time]);
  const original = {
    in: local(s.punch_in_time),
    out: local(s.punch_out_time),
    lin: local(s.lunch_in_time),
    lout: local(s.lunch_out_time),
  };
  const editDirty =
    mode === 'edit' &&
    (draft.in !== original.in ||
      draft.out !== original.out ||
      draft.lin !== original.lin ||
      draft.lout !== original.lout ||
      !!draft.reason.trim());
  useDirty(
    'kontroll-record',
    editDirty || (mode != null && mode !== 'edit' && !!text.trim()),
    T('Registreringen'),
  );
  useEffect(() => {
    if (err) errRef.current?.focus();
  }, [err]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: when the panel is opened from a payroll check
  useEffect(() => {
    if (!startEdit || !mayEdit) return;
    setMode('edit');
    requestAnimationFrame(() =>
      document.getElementById(startEdit === 'out' ? 'k-actual-out' : 'k-actual-in')?.focus(),
    );
  }, [startEdit]);
  const focusSoon = (id: string) => requestAnimationFrame(() => document.getElementById(id)?.focus());

  const waiting = r.exceptions.includes('waiting');
  const ongoing = r.bucket === 'ongoing';
  // a gap (an expected shift with no registration) is decided like a review record
  const rev = r.bucket === 'review' || r.bucket === 'gap';
  const blocked = r.kind === 'missing' || r.kind === 'open' || !!r.block;
  const valid = !blocked && !ongoing && r.bucket !== 'planned';
  const h = r.bucket === 'planned' ? null : worked(s, policy);
  const wr = workedResult(s, policy);
  const plan = plannedPaidHours(s, policy);
  const max = Math.max(plan, h ?? 0, 0.01);
  const recMin =
    s.punch_in_time && s.punch_out_time
      ? Math.round((Date.parse(s.punch_out_time) - Date.parse(s.punch_in_time)) / 60000)
      : null;
  const idx = queue.indexOf(s.id);
  const prev = idx > 0 ? queue[idx - 1] : undefined;
  const next = idx >= 0 && idx < queue.length - 1 ? queue[idx + 1] : undefined;
  const ro = !writable;

  // ── the correction's live result (D139) ──
  const draftShift: ApiShift = {
    ...s,
    punch_in_time: instant(draft.in) || null,
    punch_out_time: instant(draft.out) || null,
    lunch_in_time: instant(draft.lin) || null,
    lunch_out_time: instant(draft.lout) || null,
  };
  const draftProblem = (): string | null => {
    if (!draft.in || !draft.out) return T('Fyll inn både inn- og utstempling. Bruk bekreftede tider.');
    const g =
      Date.parse(draftShift.punch_out_time as string) - Date.parse(draftShift.punch_in_time as string);
    if (!(g > 0)) return T('Utstempling må være etter innstempling. Kontroller også datoen.');
    if (g > 7 * 24 * 3_600_000) return T('Registreringen er over sju dager. Kontroller datoene.');
    if (!!draft.lin !== !!draft.lout)
      return T('Fyll inn både start og slutt på pausen, eller la begge stå tomme.');
    if (draft.lin && draft.lout) {
      const a = Date.parse(draftShift.lunch_in_time as string);
      const b = Date.parse(draftShift.lunch_out_time as string);
      if (
        !(b > a) ||
        a < Date.parse(draftShift.punch_in_time as string) ||
        b > Date.parse(draftShift.punch_out_time as string)
      )
        return T('Pausen må ligge innenfor inn- og utstemplingen.');
    }
    return null;
  };
  const preview = () => {
    const p = draftProblem();
    if (p) return <span>{p}</span>;
    const res = workedResult(draftShift, policy);
    const gross =
      (Date.parse(draftShift.punch_out_time as string) - Date.parse(draftShift.punch_in_time as string)) /
      3_600_000;
    if (res.status !== 'ok')
      return <span>{T('Arbeidstiden kan ikke regnes ut før bedriftens pauseregel er lastet.')}</span>;
    return (
      <>
        <span>{T('Etter retting')}</span>
        <strong>{hoursText(res.minutes / 60)}</strong>
        <small>{`${num(gross)} t − ${Math.round(res.deductedMinutes)} min ${T('pause')}`}</small>
      </>
    );
  };

  async function save() {
    if (saving) return;
    const p =
      draftProblem() ??
      (draft.reason.trim().length < 3
        ? T('Skriv en begrunnelse. Opprinnelig registrering og retting beholdes i historikken.')
        : null);
    if (p) return setErr(p);
    if (
      draft.in === original.in &&
      draft.out === original.out &&
      draft.lin === original.lin &&
      draft.lout === original.lout
    )
      return setErr(T('Tidene er uendret. Avbryt rettingen og bruk «Bekreft som riktig» eller «Godkjenn».'));
    setSaving(true);
    setErr(null);
    try {
      await onCorrect({
        punch_in_time: draftShift.punch_in_time as string,
        punch_out_time: draftShift.punch_out_time as string,
        lunch_in_time: draftShift.lunch_in_time,
        lunch_out_time: draftShift.lunch_out_time,
        change_reason: draft.reason.trim(),
        if_unchanged: revisionOf(s),
      });
      setMode(null);
    } catch (e) {
      setErr(T('Rettingen ble ikke lagret. Utkastet er beholdt. {why}', { why: errorText(e) }));
    } finally {
      setSaving(false);
    }
  }

  async function decide(nextToo: boolean) {
    if (saving) return;
    const note = text.trim();
    if (!note) {
      setErr(T('Skriv en kort begrunnelse før du bekrefter.'));
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      if (mode === 'approve') {
        if (!(await onApprove(nextToo, note)))
          throw new Error(T('Registreringen ble ikke godkjent. Grunnen er beholdt.'));
      } else if (mode === 'reopen') await onReopen(note);
      else if (mode === 'clarify') await onAsk(note);
      else if (mode === 'reply') await onReply(note);
      setMode(null);
      setText('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  const open = (m: Mode, focus: string) => {
    setMode(m);
    setErr(null);
    setText('');
    focusSoon(focus);
  };
  const decisionCopy: Record<'approve' | 'clarify' | 'reply' | 'reopen', [string, string, string]> = {
    approve: [
      T('Hva har du kontrollert?'),
      T('Tider og pausetrekk beholdes. Bare godkjenningsstatus endres.'),
      T('Godkjenn uten å endre tidene'),
    ],
    clarify: [
      T('Hva trenger du å få avklart?'),
      T(
        'Registreringen venter på ansatt og kan ikke godkjennes før avklaringen er håndtert. Spørsmålet sendes til {name} som en melding i Timefly: varsel på telefonen, ellers en e-post om at det venter en melding.',
        { name },
      ),
      T('Opprett avklaring'),
    ],
    reply: [
      T('Hva svarte medarbeideren?'),
      T('Svaret registreres av deg i historikken. Arbeidstid og godkjenning endres ikke.'),
      T('Lagre mottatt svar'),
    ],
    reopen: [
      T('Hvorfor trekkes godkjenningen tilbake?'),
      T(
        'Registreringen går tilbake til kontroll og er ikke med i lønnsgrunnlaget før den godkjennes på nytt. Tidene endres ikke. I en lukket lønnsmåned avvises dette.',
      ),
      T('Trekk tilbake godkjenning'),
    ],
  };

  const caseTitle = ongoing ? T('Vakten pågår') : T(r.problem.label);
  const caseText = ongoing
    ? T('Planlagt slutt er ikke passert. Manglende utstempling regnes ikke som et avvik ennå.')
    : T(r.problem.text);
  const iv = (a: string | null | undefined, b: string | null | undefined) => {
    const x = interval(a, b);
    return x.days ? `${x.time} (${x.days})` : x.time;
  };
  const hist = history ?? [];
  const orig = originalOf(s, hist);

  const footer =
    mode === 'edit' ? (
      <div className="kpdec">
        <Button
          onClick={() => {
            // cancel drops the draft: opening the correction again starts from the saved times (#295 ONB-14)
            setMode(null);
            setErr(null);
            setDraft({ ...original, reason: '' });
          }}
          disabled={saving}
        >
          {T('Avbryt retting')}
        </Button>
        <Button tone="pri" onClick={save} disabled={saving || ro}>
          {saving ? T('Lagrer…') : T('Lagre retting')}
        </Button>
      </div>
    ) : mode ? (
      <div className="kpdec">
        <Button
          onClick={() => {
            setMode(null);
            setErr(null);
          }}
          disabled={saving}
        >
          {T('Avbryt')}
        </Button>
        <Button
          {...(mode === 'approve' ? {} : { tone: mode === 'reopen' ? ('dgr' as const) : ('pri' as const) })}
          onClick={() => decide(false)}
          disabled={saving || ro}
        >
          {saving ? T('Lagrer…') : decisionCopy[mode][2]}
        </Button>
        {mode === 'approve' && (
          <Button tone="pri" onClick={() => decide(true)} disabled={saving || ro}>
            {T('Godkjenn og neste')}
          </Button>
        )}
      </div>
    ) : (
      <>
        <div className="kprow">
          <span className="kpnav">
            <button
              type="button"
              className="btn kpnb"
              disabled={prev == null}
              aria-label={T('Forrige registrering')}
              onClick={() => prev != null && onGo(prev)}
            >
              <Icon name="chevronLeft" />
            </button>
            <span className="tnum">{idx >= 0 ? T('{a} av {b}', { a: idx + 1, b: queue.length }) : ''}</span>
            <button
              type="button"
              className="btn kpnb"
              disabled={next == null}
              aria-label={T('Neste registrering')}
              onClick={() => next != null && onGo(next)}
            >
              <Icon name="chevronRight" />
            </button>
          </span>
          <span className="kpact">
            {mayEdit && (
              <Button
                disabled={ro}
                onClick={() => {
                  setMode('edit');
                  setErr(null);
                  focusSoon('k-actual-in');
                }}
              >
                {T('Rett registrering')}
              </Button>
            )}
            {mayApprove && r.bucket !== 'approved' && r.bucket !== 'ready' && (
              <Button
                tone="pri"
                disabled={ro || !valid || waiting || busy}
                onClick={() => open('approve', 'k-decision')}
              >
                {T('Bekreft som riktig')}
              </Button>
            )}
            {mayApprove && r.bucket === 'approved' && (
              <Button disabled={ro || busy} onClick={() => open('reopen', 'k-decision')}>
                {T('Trekk tilbake godkjenning')}
              </Button>
            )}
            {mayApprove && r.bucket === 'ready' && (
              <>
                <Button disabled={ro || busy || waiting} onClick={() => onApprove(false)}>
                  {T('Godkjenn')}
                </Button>
                <Button tone="pri" disabled={ro || busy || waiting} onClick={() => onApprove(true)}>
                  {T('Godkjenn og neste')}
                </Button>
              </>
            )}
          </span>
        </div>
        <div className="kprow2">
          {mayAsk && rev && !waiting && !ongoing && (
            <button
              type="button"
              className="btn ghost kpclar"
              disabled={ro}
              onClick={() => open('clarify', 'k-decision')}
            >
              {T('Be om avklaring')}
            </button>
          )}
          <span className="sp" />
          {menu.length > 0 && (
            <Menu
              tip={T('Flere valg')}
              trigger={
                <button type="button" className="more" aria-label={T('Flere valg')}>
                  <Icon name="more" />
                </button>
              }
              items={menu}
            />
          )}
        </div>
        <p className="kpnote">
          {r.bucket === 'approved'
            ? T('Godkjent og med i lønnsgrunnlaget.')
            : waiting
              ? T('Registrer mottatt svar eller rett med bekreftede opplysninger før godkjenning.')
              : !valid && !ongoing
                ? T('Manglende eller ugyldige tider må rettes før godkjenning.')
                : T('Retting lagres først. Godkjenning er en egen handling.')}
        </p>
      </>
    );

  return (
    <DetailPanel
      label={T('Registrering')}
      restoreFocusTo={`[data-review-open="${s.id}"]`}
      title={name}
      sub={[sub, dateLong(s.start_time)].filter(Boolean).join(', ')}
      onClose={onClose}
      footer={footer}
    >
      {err && (
        <div className="kerrbox" role="alert" tabIndex={-1} ref={errRef}>
          {err}
        </div>
      )}
      {(!rev || waiting) && (
        <p className="kpill">
          <Status r={r} />
        </p>
      )}
      {clar && (
        <section className={`kclar ${clar.status}`} aria-label={T('Avklaring')} data-testid="clarification">
          <h3>{clar.status === 'open' ? T('Venter på ansatt') : T('Svar mottatt, vurder arbeidstiden')}</h3>
          <p>
            <strong>{T('Ansvarlig: {who}', { who: name })}</strong>,{' '}
            {T('følges opp av {leader}', { leader: clar.asked_by_name ?? T('leder') })}
          </p>
          <p className="kclq">{clar.question}</p>
          {clar.reply && (
            <p>
              <strong>{T('Mottatt svar:')}</strong> {clar.reply}
            </p>
          )}
          <p className="kclm">{deliveryText(clar)}</p>
          {writable && (mayEdit || mayApprove) && !mode && (
            <div className="kcla">
              {clar.status === 'open' && (
                <button type="button" className="btn ghost" onClick={() => open('reply', 'k-decision')}>
                  {T('Registrer mottatt svar')}
                </button>
              )}
              {clar.status === 'open' && clar.delivery === 'failed' && (
                <button type="button" className="btn ghost" onClick={onResend}>
                  {T('Send på nytt')}
                </button>
              )}
              <button type="button" className="btn ghost" onClick={onWithdraw}>
                {clar.status === 'open' ? T('Trekk tilbake spørsmålet') : T('Avslutt avklaringen')}
              </button>
            </div>
          )}
        </section>
      )}
      <div className={`kcase${rev ? ' warning' : ''}`}>
        <h3>{caseTitle}</h3>
        <p>{caseText}</p>
        {rev && !ongoing && <p>{T('Planen er referanse. Bekreft hva som faktisk ble arbeidet.')}</p>}
        {entries.length > 0 && (
          <ul className="tlrules">
            {entries.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="kcmp" data-testid="compare">
        <div>
          <span>{T('Planlagt etter pause')}</span>
          <strong>{hoursText(plan)}</strong>
          <div className="kbar">
            <i style={{ width: `${(plan / max) * 100}%` }} />
          </div>
          <small>
            {/uten plan/i.test(s.shiftType?.name ?? '')
              ? T('Ingen planlagt vakt')
              : `${dateShort(s.start_time)}, ${iv(s.start_time, s.end_time)}`}
          </small>
        </div>
        <div>
          <span>{T('Registrert etter pause')}</span>
          <strong className={rev ? 'attention' : undefined}>{hoursText(h)}</strong>
          <div className="kbar actual">
            <i style={{ width: `${h == null ? 0 : (h / max) * 100}%` }} />
          </div>
          <small>
            {s.punch_in_time
              ? `${dateShort(s.punch_in_time)}, ${iv(s.punch_in_time, s.punch_out_time)}`
              : NONE}
          </small>
        </div>
      </div>
      {mode === 'edit' ? (
        <form
          className="kedit"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <h3>{T('Rett faktisk arbeidstid')}</h3>
          <p>{T('Planlagte tider endres ikke. Bruk bekreftede datoer og klokkeslett.')}</p>
          <div className="kdates">
            <label htmlFor="k-actual-in">
              {T('Stemplet inn')}
              <input
                id="k-actual-in"
                className="inp"
                type="datetime-local"
                value={draft.in}
                onChange={(e) => setDraft({ ...draft, in: e.target.value })}
              />
            </label>
            <label htmlFor="k-actual-out">
              {T('Stemplet ut')}
              <input
                id="k-actual-out"
                className="inp"
                type="datetime-local"
                value={draft.out}
                onChange={(e) => setDraft({ ...draft, out: e.target.value })}
              />
            </label>
            <label htmlFor="k-lunch-in">
              {T('Pause start')}
              <input
                id="k-lunch-in"
                className="inp"
                type="datetime-local"
                value={draft.lin}
                onChange={(e) => setDraft({ ...draft, lin: e.target.value })}
              />
            </label>
            <label htmlFor="k-lunch-out">
              {T('Pause slutt')}
              <input
                id="k-lunch-out"
                className="inp"
                type="datetime-local"
                value={draft.lout}
                onChange={(e) => setDraft({ ...draft, lout: e.target.value })}
              />
            </label>
          </div>
          <p className="khint">
            {T(
              'Pausetrekket regnes etter bedriftens pauseregel. Fyll inn pausen bare hvis den ble stemplet.',
            )}
          </p>
          <div className="kprev" aria-live="polite">
            {preview()}
          </div>
          <label htmlFor="k-reason">
            {T('Begrunnelse for rettingen')}
            <textarea
              id="k-reason"
              className="inp"
              rows={3}
              maxLength={500}
              placeholder={T('Hva er kontrollert, og hvorfor endres registreringen?')}
              value={draft.reason}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            />
          </label>
          <p className="khint">
            {T('Endringene lagres først når du velger «Lagre retting». Godkjenning gjenstår etter lagring.')}
          </p>
        </form>
      ) : (
        <dl className="kfacts">
          <div>
            <dt>{T('Inn')}</dt>
            <dd>{dateTimeLong(s.punch_in_time) ?? T('Ikke registrert')}</dd>
          </div>
          <div>
            <dt>{T('Ut')}</dt>
            <dd>{dateTimeLong(s.punch_out_time) ?? T('Ikke registrert')}</dd>
          </div>
          <div>
            <dt>{T('Registrert varighet')}</dt>
            <dd>{recMin == null ? NONE : hoursText(recMin / 60)}</dd>
          </div>
          {s.lunch_in_time && (
            <div>
              <dt>{T('Pause registrert')}</dt>
              <dd>{`${clockOf(s.lunch_in_time)} – ${clockOf(s.lunch_out_time)}`}</dd>
            </div>
          )}
          <div>
            <dt>{T('Pausetrekk')}</dt>
            <dd>{wr.status === 'ok' ? `${Math.round(wr.deductedMinutes)} min` : T('Ikke registrert')}</dd>
          </div>
          {extraFacts}
          <div className="ktotal">
            <dt>{T('Arbeidstid etter pause')}</dt>
            <dd aria-label={h == null ? T('ukjent') : undefined}>{hoursText(h)}</dd>
          </div>
        </dl>
      )}
      {mode && mode !== 'edit' && (
        <div className="kdecision">
          <label htmlFor="k-decision">{decisionCopy[mode][0]}</label>
          <textarea
            id="k-decision"
            className="inp"
            rows={3}
            maxLength={500}
            placeholder={T('Skriv her')}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <p>{decisionCopy[mode][1]}</p>
        </div>
      )}
      <details className="kaudit">
        <summary>
          <span>
            <Icon name="plus" size={16} />
            {T('Opprinnelig registrering og historikk')}
          </span>
          <span className="kcount">{history === null ? '…' : hist.length}</span>
        </summary>
        <dl className="kfacts">
          <div>
            <dt>{T('Opprinnelig inn')}</dt>
            <dd>{dateTimeLong(orig.punch_in_time) ?? T('Ikke registrert')}</dd>
          </div>
          <div>
            <dt>{T('Opprinnelig ut')}</dt>
            <dd>{dateTimeLong(orig.punch_out_time) ?? T('Ikke registrert')}</dd>
          </div>
          <div>
            <dt>{T('Opprinnelig pause')}</dt>
            <dd>
              {orig.lunch_in_time
                ? `${clockOf(orig.lunch_in_time)} – ${clockOf(orig.lunch_out_time)}`
                : T('Ikke registrert')}
            </dd>
          </div>
        </dl>
        {history === undefined ? (
          <p>{T('Kunne ikke hente historikken.')}</p>
        ) : (
          <History
            rows={hist}
            day={oslo(s.start_time)?.date ?? ''}
            empty={T('Ingen endringer. Tidene står slik de ble registrert.')}
          />
        )}
      </details>
      <p className="kref">{T('Registrering {id}', { id: s.id })}</p>
    </DetailPanel>
  );
}
